<template>
  <div class="flex flex-col">
    <div class="space-y-3 text-lg">
      <HighlightChange
        :changeType="gradeDiff">
        <div class="flex items-center gap-3">
          <div class="font-bold">Grade:</div>
          <span>{{ grade }}</span>
        </div>
      </HighlightChange>
      <HighlightChange
        :changeType="programDiff">
        <div class="flex items-center gap-3">
          <div class="font-bold">Program:</div>
          <span>{{ program }}</span>
        </div>
      </HighlightChange>
      <HighlightChange
        :changeType="stateReportCodeDiff"
        v-if="stateReportCodeType">
        <div class="flex items-center gap-3">
          <div class="font-bold">
            State Report Code:
          </div>
          <span>{{ `${stateReportCodeType}:${stateReportCode}` }}</span>
        </div>
      </HighlightChange>
      <HighlightChange
        :changeType="studentReleaseDiff">
        <div class="flex items-center gap-3">
          <div class="font-bold">
            Student Release:
          </div>
          <span>{{ studentRelease }}</span>
        </div>
      </HighlightChange>
      <div class="space-y-3 pt-2 mt-2 border-t-2 border-gray-200"></div>
      <HighlightChange
        :changeType="accommodationsDiff">
        <div>
          <div class="text-lg font-bold"
            v-if="!accommodations">
            No accommodations
          </div>
        </div>
        <div class="text-lg space-y-3" v-if="accommodations">
          <div class="font-bold">
            Accommodations:
          </div>
          <div class="text-base rounded-md !shadow bg-white py-2 px-3">
            {{ accommodations }}
          </div>
          <div v-if="vestInfo">
            <SafetyVestInfo
              :vestInfo="vestInfo"
            />
          </div>
        </div>
      </HighlightChange>
      <HighlightChange
        :changeType="medicalNotesDiff">
        <div>
          <div class="text-lg font-bold"
            v-if="!medicalNotes">
            No medical information
          </div>
        </div>
        <div class="text-lg space-y-3" v-if="medicalNotes">
          <div class="font-bold">
            Medical Information:
          </div>
          <div class="text-base rounded-md !shadow bg-white py-2 px-3">
            {{ medicalNotes }}
          </div>
        </div>
      </HighlightChange>
      <HighlightChange
        :changeType="additionalNotesDiff">
        <div class="text-lg font-bold"
          v-if="!additionalNotes">
          No additional transportation information
        </div>
        <div class="text-lg space-y-3" v-if="additionalNotes">
          <div class="font-bold">
            Additional Transportation Information:
          </div>
          <div class="text-base rounded-md !shadow bg-white py-2 px-3">
            <div v-for="note in additionalNotes" :key="note.id">
              <span class="font-italic font-bold pr-2">
                {{ note.id }}
              </span>
              <span>
                {{ note.value }}
              </span>
            </div>
          </div>
        </div>
      </HighlightChange>
    </div>
  </div>
</template>

<script>
import { map, camelCase, isEmpty } from 'lodash'
import scheduleUtil from '../../../services/utils/schedule.js'

import DROPDOWN_LISTS from '../../DropdownLists.json'
import ACCOMMODATIONS_MAP from '../../AccommodationsMap.json'

import HighlightChange from '../utilities/HighlightChange.vue'

import SafetyVestInfo from '../../StudentHistory/common/SafetyVestInfo.vue'

export default {
  components: {
    HighlightChange,
    SafetyVestInfo,
  },
  props: {
    studentInfo: Object,
    studentInfoDiff: Object,
  },
  computed: {
    grade() {
      if (this.studentInfo?.grade) {
        return DROPDOWN_LISTS.gradeList[this.studentInfo?.grade]
      } else {
        return ''
      }
    },
    gradeDiff() {
      return scheduleUtil.mapDiffObject(this.studentInfoDiff?.grade)
    },
    program() {
      return this.studentInfo?.program?.description
    },
    programDiff() {
      return scheduleUtil.mapDiffObject(this.studentInfoDiff?.program)
    },
    stateReportCodeType() {
      return this.studentInfo?.state_report_code_type
    },
    stateReportCode() {
      return this.studentInfo?.state_report_code
    },
    stateReportCodeDiff() {
      return scheduleUtil.mapDiffObject(this.studentInfoDiff?.state_report_code)
    },
    medicalNotes() {
      return this.studentInfo?.medical_notes
    },
    medicalNotesDiff() {
      return scheduleUtil.mapDiffObject(this.studentInfoDiff?.medical_notes)
    },
    accommodations() {
      if (!this.studentInfo) {
        return undefined
      }
      const infoNames = [
        'info_aide',
        'info_wheelchair',
        'info_solo',
        'info_enclosed_vehicle',
        'info_booster_seat',
        'info_safety_vest',
        'info_safety_belt_locking_cover',
        'info_car_seat',
      ]
      let targetCheckObj
      if (this.studentInfo.accommodations) {
        targetCheckObj = this.studentInfo.accommodations
      }
      else {
        targetCheckObj = this.studentInfo
      }
      let values = []
      for (let infoName of infoNames) {
        const value = targetCheckObj[infoName]
        if (value) {
          infoName = camelCase(infoName)
          if (infoName === 'infoAide') {
            if (value in ACCOMMODATIONS_MAP.infoAide) {
              values.push(ACCOMMODATIONS_MAP.infoAide[value])
            }
          } else {
            values.push(ACCOMMODATIONS_MAP[infoName])
          }
        }
      }
      if (values.length > 0) {
        return values.join(', ')
      }
      return ''
    },
    accommodationsDiff() {
      if (!this.studentInfoDiff) {
        return undefined
      }
      const infoNames = [
        'info_aide',
        'info_wheelchair',
        'info_solo',
        'info_enclosed_vehicle',
        'info_booster_seat',
        'info_safety_vest',
        'info_safety_belt_locking_cover',
        'info_car_seat',
      ]
      let targetCheckObj
      if (this.studentInfoDiff.accommodations) {
        targetCheckObj = this.studentInfoDiff.accommodations
      }
      else {
        targetCheckObj = this.studentInfoDiff
      }
      for (let infoName of infoNames) {
        const value = targetCheckObj[infoName]
        if (scheduleUtil.mapDiffObject(value)) {
          return 'change'
        }
      }
      return undefined
    },
    vestInfo() {
      if (!this.studentInfo.vest_info) {
        return
      }
      return {
        needVestProvided: this.studentInfo.vest_info['need_vest_provided'],
        manufacturer: this.studentInfo.vest_info['manufacturer'],
        modelNumber: this.studentInfo.vest_info['model_number'],
        vestSize: this.studentInfo.vest_info['vest_size'],
      }
    },
    vestInfoDiff() {
      return scheduleUtil.mapDiffObject(this.studentInfoDiff?.vest_info)
    },
    studentRelease() {
      if (!this.studentInfo) {
        return undefined
      }

      if (this.studentInfo.student_release === 'I')
        return 'Release Independently'
      else if (this.studentInfo.student_release === 'e2e')
        return 'Eye-to-Eye Release'
      else if (this.studentInfo.student_release === 'h2h')
        return 'Hand-to-Hand Transfer'
      else if (this.studentInfo.student_release === 'mse')
        return 'Must See Enter'
      else return 'Error'
    },
    studentReleaseDiff() {
      return scheduleUtil.mapDiffObject(this.studentInfoDiff?.student_release)
    },
    additionalNotes() {
      if (this.studentInfo?.notes === undefined) return undefined

      let notesObj = JSON.parse(this.studentInfo?.notes)
      for (const key in notesObj) {
        if (!notesObj[key]) {
          delete notesObj[key]
        }
      }
      if (!isEmpty(notesObj)) {
        return map(notesObj, (value, key) => {
          return {
            id: key,
            value: value,
          }
        })
      } else {
        return undefined
      }
    },
    additionalNotesDiff() {
      return scheduleUtil.mapDiffObject(this.studentInfoDiff?.notes)
    },
  },
}
</script>
